import React, { useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Modal, ScrollView, Text as RNText } from 'react-native';
import { Text } from '@/components';
import { Globe, Lock, Users, Backpack, ChevronDown } from 'lucide-react-native';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { useMyGroups } from '@/lib/queries/groups';
import type { Visibility, Group } from '@/types/group';
import { useTranslation } from 'react-i18next';

const OPTIONS: { value: Visibility; icon: any; labelKey: string }[] = [
  { value: 'public', icon: Globe, labelKey: 'visibility.public' },
  { value: 'private', icon: Lock, labelKey: 'visibility.private' },
  { value: 'family', icon: Users, labelKey: 'visibility.family' },
  { value: 'pack', icon: Backpack, labelKey: 'visibility.pack' },
];

interface Props {
  token?: string;
  visibility: Visibility;
  sharedGroupId?: string | null;
  onChange: (visibility: Visibility, sharedGroupId?: string | null) => void;
  /** Compact row trigger (default) vs. full inline list of options. */
  compact?: boolean;
}

export default function VisibilitySelector({ token, visibility, sharedGroupId, onChange, compact = true }: Props) {
  const { t } = useTranslation();
  const packsQuery = useMyGroups(token, 'pack');
  const packs: Group[] = packsQuery.data ?? [];
  const [open, setOpen] = useState(false);
  const [choosingPack, setChoosingPack] = useState(false);

  const selected = OPTIONS.find((o) => o.value === visibility) ?? OPTIONS[0];
  const SelectedIcon = selected.icon;
  const sharedPackName = packs.find((p) => p.id === sharedGroupId)?.name;

  const label = visibility === 'pack' && sharedPackName
    ? `${t('visibility.pack')}: ${sharedPackName}`
    : t(selected.labelKey);

  const pick = (v: Visibility) => {
    if (v === 'pack') {
      // Don't send pack-without-group — backend 422s on null shared_group_id.
      // Show the pack list and let the user pick one first.
      if (packs.length === 0) return; // hint is shown inline
      setChoosingPack(true);
      return;
    }
    setChoosingPack(false);
    onChange(v, null);
    setOpen(false);
  };

  const pickPack = (packId: string) => {
    onChange('pack', packId);
    setChoosingPack(false);
    setOpen(false);
  };

  const close = () => {
    setChoosingPack(false);
    setOpen(false);
  };

  const trigger = (
    <TouchableOpacity style={styles.trigger} onPress={() => setOpen(true)} disabled={!token}>
      <SelectedIcon size={16} color={theme.accentPrimary} />
      <Text style={styles.triggerText} numberOfLines={1}>{label}</Text>
      <ChevronDown size={16} color={theme.textMuted} />
    </TouchableOpacity>
  );

  if (!compact) return trigger;

  return (
    <View>
      {trigger}
      <Modal visible={open} transparent animationType="slide" onRequestClose={close}>
        <TouchableOpacity style={styles.overlay} onPress={close} activeOpacity={1}>
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>{t('visibility.audience')}</Text>
            <ScrollView>
              {OPTIONS.map((o) => {
                const Icon = o.icon;
                const active = o.value === visibility;
                const disabled = o.value === 'family' && false; // family always available (backend auto-targets)
                return (
                  <TouchableOpacity
                    key={o.value}
                    style={[styles.option, active && styles.optionActive]}
                    onPress={() => pick(o.value)}
                    disabled={disabled}
                  >
                    <Icon size={20} color={active ? theme.accentPrimary : theme.textMuted} />
                    <View style={{ flex: 1, marginLeft: 12 }}>
                      <Text style={styles.optionLabel}>{t(o.labelKey)}</Text>
                      {o.value === 'family' && <Text style={styles.optionHint}>{t('visibility.familyHint')}</Text>}
                      {o.value === 'pack' && packs.length === 0 && (
                        <Text style={styles.optionHint}>{t('visibility.noPacks')}</Text>
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}

              {(choosingPack || visibility === 'pack') && packs.length > 0 && (
                <View style={styles.packList}>
                  <Text style={styles.packListTitle}>{t('visibility.choosePack')}</Text>
                  {packs.map((p) => (
                    <TouchableOpacity
                      key={p.id}
                      style={[styles.packRow, p.id === sharedGroupId && styles.packRowActive]}
                      onPress={() => pickPack(p.id)}
                    >
                      <Backpack size={18} color={theme.accentPrimary} />
                      <Text style={styles.packName}>{p.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: theme.backgroundSecondary,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
    alignSelf: 'flex-start',
  },
  triggerText: { ...Typography.body(theme.textPrimary), fontSize: 13, maxWidth: 200 },
  overlay: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.45)' },
  sheet: {
    backgroundColor: theme.backgroundPrimary,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 20,
    maxHeight: '70%',
  },
  sheetTitle: { ...Typography.body(theme.textPrimary), fontSize: 18, fontWeight: '700', marginBottom: 16 },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRadius: 12,
    marginVertical: 2,
    backgroundColor: theme.backgroundSecondary,
  },
  optionActive: { borderColor: theme.accentPrimary, borderWidth: 1 },
  optionLabel: { ...Typography.body(theme.textPrimary), fontSize: 15, fontWeight: '600' },
  optionHint: { ...Typography.caption(theme.textMuted), fontSize: 12, marginTop: 2 },
  packList: { marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: theme.borderSubtle },
  packListTitle: { ...Typography.label(theme.textMuted), marginBottom: 8 },
  packRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: theme.backgroundSecondary,
    marginVertical: 2,
  },
  packRowActive: { borderColor: theme.accentPrimary, borderWidth: 1 },
  packName: { ...Typography.body(theme.textPrimary), flex: 1 },
});